import React from "react";
import { AiLoadError, useAiRequestGuard, withAiAgentScope } from "./aiAgentRequests";
import {
  ChevronDown,
  ChevronRight,
  RefreshCcw,
  RotateCcw,
  Search,
  Zap,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import {
  getAiMediaAssetSenders,
  getAiLeadQualificationDebug,
  getCrmContactsAll,
  getLeadQualificationReminderJobs,
  resetAiLeadQualificationSession,
} from "./api/aiAgentApi";

const EMPTY_ARRAY = [];

// ─── helpers ────────────────────────────────────────────────────────────────

function fmtDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

function relativeTime(value) {
  if (!value) return null;
  const diff = Date.now() - new Date(value).getTime();
  if (diff < 0) return null;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function tryParseJson(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function safeJsonText(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw !== "string") {
    try {
      return JSON.stringify(raw, null, 2);
    } catch {
      return String(raw);
    }
  }

  const parsed = tryParseJson(raw);
  if (parsed == null) return raw;
  if (typeof parsed === "string") return parsed;
  try {
    return JSON.stringify(parsed, null, 2);
  } catch {
    return raw;
  }
}

function JsonKv({ raw, emptyLabel }) {
  const parsed = tryParseJson(raw);
  if (!parsed) return <span className="text-xs text-slate-400">{emptyLabel}</span>;
  const entries = Object.entries(parsed);
  if (entries.length === 0) return <span className="text-xs text-slate-400">{emptyLabel}</span>;
  return (
    <div className="space-y-1">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-2 text-xs">
          <span className="min-w-0 shrink-0 font-medium text-slate-600">{k}:</span>
          <span className="min-w-0 break-all text-slate-800">{String(v)}</span>
        </div>
      ))}
    </div>
  );
}

function JsonList({ raw, emptyLabel }) {
  const parsed = tryParseJson(raw);
  if (!Array.isArray(parsed) || parsed.length === 0)
    return <span className="text-xs text-slate-400">{emptyLabel}</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {parsed.map((item, i) => (
        <span
          key={i}
          className="rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800"
        >
          {String(item)}
        </span>
      ))}
    </div>
  );
}

function JsonPretty({ raw, emptyLabel }) {
  const text = safeJsonText(raw);
  if (!text) return <span className="text-xs text-slate-400">{emptyLabel}</span>;

  return (
    <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded bg-white p-2 font-mono text-[11px] leading-5 text-slate-700 ring-1 ring-slate-200">
      {text}
    </pre>
  );
}

const DECISION_TRACE_FIELDS = [
  ["fieldSavePlanTrace", "Field save plan trace"],
  ["clarificationPlanTrace", "Clarification plan trace"],
  ["answerResumePlanTrace", "Answer resume plan trace"],
  ["refusalPolicyTrace", "Refusal policy trace"],
  ["clarificationTrace", "Clarification trace"],
];

function collectDecisionTraces(result, actionLogs) {
  const traces = {};

  for (const [key, value] of Object.entries(result || {})) {
    if (DECISION_TRACE_FIELDS.some(([traceKey]) => traceKey === key) && value != null) {
      traces[key] = value;
    }
  }

  for (const log of Array.isArray(actionLogs) ? actionLogs : []) {
    const parsed = tryParseJson(log?.plannerRawJson);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) continue;

    for (const [key] of DECISION_TRACE_FIELDS) {
      if (traces[key] != null) continue;
      const value = parsed[key];
      if (value != null && value !== "") traces[key] = value;
    }
  }

  return traces;
}

// ─── badge helpers ───────────────────────────────────────────────────────────

const STATUS_COLORS = {
  Active: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Completed: "bg-sky-50 text-sky-700 ring-sky-200",
  Expired: "bg-slate-100 text-slate-600 ring-slate-200",
  Reset: "bg-amber-50 text-amber-700 ring-amber-200",
};

const SEND_COLORS = {
  Succeeded: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Failed: "bg-rose-50 text-rose-700 ring-rose-200",
  Skipped: "bg-slate-100 text-slate-500 ring-slate-200",
  Attempting: "bg-amber-50 text-amber-700 ring-amber-200",
  NotAttempted: "bg-slate-50 text-slate-500 ring-slate-200",
};

const REMINDER_JOB_COLORS = {
  Pending: "bg-amber-50 text-amber-700 ring-amber-200",
  Processing: "bg-blue-50 text-blue-700 ring-blue-200",
  Sent: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Skipped: "bg-slate-100 text-slate-500 ring-slate-200",
  Failed: "bg-rose-50 text-rose-700 ring-rose-200",
  Cancelled: "bg-slate-100 text-slate-500 ring-slate-200",
};

const ACTION_COLORS = {
  AskNextQuestion: "bg-violet-50 text-violet-700",
  Handover: "bg-rose-50 text-rose-700",
  LeadCompleted: "bg-emerald-50 text-emerald-700",
  SafeFallback: "bg-amber-50 text-amber-700",
  AnswerAndResume: "bg-sky-50 text-sky-700",
  RequestPhoto: "bg-indigo-50 text-indigo-700",
  AskCallbackTime: "bg-teal-50 text-teal-700",
  SendMedia: "bg-fuchsia-50 text-fuchsia-700",
};

function Badge({ label, colorClass }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${colorClass}`}
    >
      {label}
    </span>
  );
}

function StatusBadge({ value }) {
  const cls = STATUS_COLORS[value] ?? "bg-slate-100 text-slate-600 ring-slate-200";
  return <Badge label={value || "Unknown"} colorClass={cls} />;
}

function SendBadge({ value }) {
  const cls = SEND_COLORS[value] ?? "bg-slate-100 text-slate-500 ring-slate-200";
  return <Badge label={value || "NotAttempted"} colorClass={cls} />;
}

function ReminderJobBadge({ value }) {
  const cls = REMINDER_JOB_COLORS[value] ?? "bg-slate-100 text-slate-600 ring-slate-200";
  return <Badge label={value || "Unknown"} colorClass={cls} />;
}

function ActionBadge({ value }) {
  const cls =
    (value && ACTION_COLORS[value]) ?? "bg-slate-100 text-slate-600";
  return (
    <span
      className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ${cls}`}
    >
      {value || "—"}
    </span>
  );
}

// ─── section chrome ──────────────────────────────────────────────────────────

function Panel({ children, className = "" }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}>
      {children}
    </div>
  );
}

function PanelHeader({ title, subtitle, actions }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
      <div>
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

function StatCard({ label, value, mono }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className={`text-sm font-medium text-slate-900 ${mono ? "font-mono" : ""}`}>
        {value ?? <span className="text-slate-400">—</span>}
      </div>
    </div>
  );
}

function ReminderJobRow({ job }) {
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3 align-top">
        <div className="text-xs font-medium text-slate-700">{relativeTime(job.createdAtUtc)}</div>
        <div className="text-xs text-slate-400">{fmtDate(job.createdAtUtc)}</div>
        {job.dueAtUtc && (
          <div className="mt-1 text-xs text-slate-500">
            Due: <span className="font-medium text-slate-700">{fmtDate(job.dueAtUtc)}</span>
          </div>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <ReminderJobBadge value={job.status} />
      </td>
      <td className="px-4 py-3 align-top text-xs text-slate-700">{job.sequence ?? "—"}</td>
      <td className="px-4 py-3 align-top text-xs text-slate-700">
        {job.lastAskedField || <span className="text-slate-400">—</span>}
      </td>
      <td className="px-4 py-3 align-top">
        <div className="max-w-xs text-xs text-slate-700 line-clamp-2">
          {job.pendingQuestionTextPreview || <span className="text-slate-400">—</span>}
        </div>
      </td>
      <td className="px-4 py-3 align-top text-xs text-slate-700">{job.attemptCount ?? 0}</td>
      <td className="px-4 py-3 align-top">
        <div className="max-w-xs text-xs text-rose-600 line-clamp-2">
          {job.failureReason || <span className="text-slate-400">—</span>}
        </div>
      </td>
      <td className="px-4 py-3 align-top text-xs text-slate-700">
        <div className="font-mono">{job.contactId || "—"}</div>
        <div className="mt-0.5 text-slate-400">{job.phoneNumberId || "—"}</div>
      </td>
      <td className="px-4 py-3 align-top text-xs text-slate-700 font-mono">
        {job.sessionId || "—"}
      </td>
      <td className="px-4 py-3 align-top text-xs text-slate-700 font-mono">
        {job.outboundQuestionMessageLogId || "—"}
      </td>
        <td className="px-4 py-3 align-top text-xs text-slate-700 font-mono">
          {job.sentTextMessageLogId || "—"}
        </td>
        <td className="px-4 py-3 align-top text-xs text-slate-700 font-mono">
          {job.sentMediaMessageLogId || "—"}
        </td>
        <td className="px-4 py-3 align-top text-xs text-slate-700">
          {job.nextAttemptAtUtc ? fmtDate(job.nextAttemptAtUtc) : <span className="text-slate-400">—</span>}
        </td>
    </tr>
  );
}

// ─── action log row ──────────────────────────────────────────────────────────

function ActionLogRow({ log }) {
  const [open, setOpen] = React.useState(false);
  const hasPlannerData = log.plannerMode && log.plannerMode !== "Off";
  const hasNrData = log.naturalReplyMode && log.naturalReplyMode !== "Off";
  const hasError = log.sendErrorCode || log.sendErrorMessage;

  return (
    <>
      <tr
        className={`cursor-pointer hover:bg-slate-50 ${open ? "bg-slate-50" : ""}`}
        onClick={() => setOpen(o => !o)}
      >
        {/* time */}
        <td className="whitespace-nowrap px-4 py-3 align-top">
          <div className="text-xs font-medium text-slate-700">
            {relativeTime(log.createdAtUtc)}
          </div>
          <div className="text-xs text-slate-400">{fmtDate(log.createdAtUtc)}</div>
        </td>

        {/* intent + step */}
        <td className="px-4 py-3 align-top">
          <div className="text-xs font-medium text-slate-800">
            {log.detectedIntent || <span className="text-slate-400">—</span>}
          </div>
          {log.currentStepBefore && (
            <div className="mt-0.5 text-xs text-slate-400">
              {log.currentStepBefore}
              {log.currentStepAfter && log.currentStepAfter !== log.currentStepBefore && (
                <span className="text-slate-300"> → {log.currentStepAfter}</span>
              )}
            </div>
          )}
        </td>

        {/* action */}
        <td className="px-4 py-3 align-top">
          <ActionBadge value={log.selectedAction} />
          {log.actionReason && (
            <div className="mt-1 text-xs text-slate-400 max-w-[160px] truncate" title={log.actionReason}>
              {log.actionReason}
            </div>
          )}
        </td>

        {/* preview */}
        <td className="px-4 py-3 align-top">
          <div className="max-w-xs text-xs text-slate-700 line-clamp-2">
            {log.responsePreview || <span className="text-slate-400">No preview</span>}
          </div>
          {log.responseType && (
            <div className="mt-0.5 text-xs text-slate-400">{log.responseType}</div>
          )}
        </td>

        {/* send */}
        <td className="px-4 py-3 align-top">
          <SendBadge value={log.sendStatus} />
          {hasError && (
            <div className="mt-1 text-xs text-rose-600 max-w-[120px] truncate">
              {log.sendErrorCode}
            </div>
          )}
        </td>

        {/* expand toggle */}
        <td className="px-3 py-3 align-top text-slate-400">
          {open ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </td>
      </tr>

      {open && (
        <tr className="bg-slate-50">
          <td colSpan={6} className="border-t border-slate-100 px-4 pb-4 pt-3">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">

              {/* IDs */}
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  IDs
                </div>
                <div className="space-y-1 text-xs">
                  {[
                    ["Log ID", log.id],
                    ["Session", log.sessionId],
                    ["Inbound msg", log.inboundMessageLogId],
                    ["Fallback log", log.aiFallbackDecisionLogId],
                    ["AutoReply log", log.aiAutoReplyDecisionLogId],
                  ].map(([label, val]) =>
                    val ? (
                      <div key={label} className="flex gap-1.5">
                        <span className="w-24 shrink-0 text-slate-500">{label}</span>
                        <span className="truncate font-mono text-slate-700" title={val}>{val}</span>
                      </div>
                    ) : null
                  )}
                </div>
              </div>

              {/* extracted fields + send detail */}
              <div className="space-y-3">
                <div>
                  <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Extracted fields
                  </div>
                  <JsonKv raw={log.extractedFieldsJson} emptyLabel="None extracted" />
                </div>
                {(log.finalDecision || hasError) && (
                  <div>
                    <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Send detail
                    </div>
                    <div className="space-y-0.5 text-xs">
                      {log.finalDecision && (
                        <div className="text-slate-600">{log.finalDecision}</div>
                      )}
                      {hasError && (
                        <div className="text-rose-600">
                          {log.sendErrorCode}: {log.sendErrorMessage}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* planner + NR */}
              <div className="space-y-3">
                {hasPlannerData && (
                  <div>
                    <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      <Zap className="h-3 w-3" />
                      Planner ({log.plannerMode})
                    </div>
                    <div className="space-y-0.5 text-xs">
                      {[
                        ["Intent", log.plannerIntent],
                        ["Confidence", log.plannerConfidence != null ? `${log.plannerConfidence}%` : null],
                        ["Next field", log.plannerNextFieldKey],
                        ["Response goal", log.plannerResponseGoal],
                        ["Media intent", log.plannerMediaIntent],
                        ["Validation", log.plannerValidationStatus],
                        ["KB query", log.plannerKbQuery],
                        ["Failure", log.plannerValidationFailureReason],
                        ["Decision", log.deterministicPlannerDecision],
                      ].map(([label, val]) =>
                        val ? (
                          <div key={label} className="flex gap-1.5">
                            <span className="w-20 shrink-0 text-slate-500">{label}</span>
                            <span
                              className={`text-slate-700 ${
                                label === "Validation" &&
                                val !== "Valid"
                                  ? "font-medium text-amber-700"
                                  : ""
                              }`}
                            >
                              {val}
                            </span>
                          </div>
                        ) : null
                      )}
                      {log.plannerNeedsKbAnswer && (
                        <div className="text-sky-600">Needs KB answer</div>
                      )}
                    </div>
                  </div>
                )}
                {hasNrData && (
                  <div>
                    <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Natural Reply ({log.naturalReplyMode})
                    </div>
                    {log.naturalReplyPreview && (
                      <div className="rounded bg-violet-50 p-2 text-xs text-violet-800">
                        {log.naturalReplyPreview}
                      </div>
                    )}
                    {log.naturalReplyRejectReason && (
                      <div className="mt-1 text-xs text-rose-600">
                        Rejected: {log.naturalReplyRejectReason}
                      </div>
                    )}
                  </div>
                )}
                {log.plannerRawJson && (
                  <div>
                    <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Decision trace
                    </div>
                    <JsonPretty raw={log.plannerRawJson} emptyLabel="No trace available" />
                  </div>
                )}
                {log.responsePreview && (
                  <div>
                    <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Full response preview
                    </div>
                    <div className="whitespace-pre-wrap rounded bg-white p-2 text-xs text-slate-700 ring-1 ring-slate-200">
                      {log.responsePreview}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── page ────────────────────────────────────────────────────────────────────

function AiLeadQualificationDebugPage() {
  const { can, hasAllAccess } = useAuth() || {};
  const canManage =
    Boolean(hasAllAccess) ||
    (typeof can === "function" && can(FK.AI_AUTO_REPLY_MANAGE));

  const [contactId, setContactId] = React.useState(() => new URLSearchParams(window.location.search).get("contactId") || "");
  const [phoneNumberId, setPhoneNumberId] = React.useState(() => new URLSearchParams(window.location.search).get("phoneNumberId") || "");
  const [contacts, setContacts] = React.useState([]);
  const [senders, setSenders] = React.useState([]);
  const [contactsLoading, setContactsLoading] = React.useState(false);
  const [sendersLoading, setSendersLoading] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [resetting, setResetting] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [searched, setSearched] = React.useState(false);
  const [reminderJobs, setReminderJobs] = React.useState([]);
  const [reminderJobsLoading, setReminderJobsLoading] = React.useState(false);
  const [reminderJobsError, setReminderJobsError] = React.useState("");
  const [reminderJobsStatus, setReminderJobsStatus] = React.useState("");
  const [reminderJobsPage, setReminderJobsPage] = React.useState(1);
  const [reminderJobsPageSize] = React.useState(10);
  const [reminderJobsTotal, setReminderJobsTotal] = React.useState(0);
  const [loadError, setLoadError] = React.useState("");
  const [contactsError, setContactsError] = React.useState("");
  const [sendersError, setSendersError] = React.useState("");
  const request = useAiRequestGuard({debug: [contactId, phoneNumberId], jobs: [contactId, phoneNumberId]});
  React.useEffect(() => {
    setResult(null); setSearched(false); setLoading(false); setLoadError("");
    setReminderJobs([]); setReminderJobsTotal(0); setReminderJobsLoading(false); setReminderJobsError("");
  }, [contactId, phoneNumberId]);

  const normalizedContacts = React.useMemo(() => {
    return (Array.isArray(contacts) ? contacts : [])
      .map(contact => ({
        contactId: contact?.id || contact?.contactId || "",
        phoneNumber: contact?.phoneNumber || contact?.phone || "",
        name: contact?.name || contact?.fullName || "Unnamed contact",
      }))
      .filter(item => item.contactId && item.phoneNumber);
  }, [contacts]);

  const normalizedSenders = React.useMemo(() => {
    return (Array.isArray(senders) ? senders : [])
      .map(sender => ({
        phoneNumberId: sender?.phoneNumberId || sender?.phone_number_id || "",
        label:
          sender?.label ||
          sender?.displayName ||
          sender?.businessNumber ||
          "",
      }))
      .filter(item => item.phoneNumberId)
      .map(item => {
        const sameAsId = item.label && item.label.trim() === item.phoneNumberId.trim();
        return {
          ...item,
          displayLabel: item.label && !sameAsId ? item.label : item.phoneNumberId,
        };
      });
  }, [senders]);

  const loadContacts = React.useCallback(async () => {
    const current = request("contacts"); setContactsError(""); setContacts([]);
    setContactsLoading(true);
    try {
      const rows = await getCrmContactsAll();
      if (!current()) return;
      setContacts(Array.isArray(rows) ? rows : []);
    } catch (err) {
      if (current()) setContactsError(err?.response?.data?.message || "Failed to load contacts.");
    } finally {
      if (current()) setContactsLoading(false);
    }
  }, [request]);

  const loadSenders = React.useCallback(async () => {
    const current = request("senders"); setSendersError(""); setSenders([]);
    setSendersLoading(true);
    try {
      const rows = await getAiMediaAssetSenders();
      if (!current()) return;
      setSenders(Array.isArray(rows) ? rows : []);
    } catch (err) {
      if (current()) setSendersError(err?.response?.data?.message || "Failed to load WhatsApp senders.");
    } finally {
      if (current()) setSendersLoading(false);
    }
  }, [request]);

  const loadReminderJobs = React.useCallback(
    async (flowDefinitionId, options = {}) => {
      const current = request("jobs");
      if (!flowDefinitionId) {
        setReminderJobs([]);
        setReminderJobsTotal(0);
        setReminderJobsError("");
        return;
      }

      const page = Math.max(1, Number(options.page) || reminderJobsPage || 1);
      const status =
        typeof options.status === "string" ? options.status : reminderJobsStatus;

      setReminderJobsLoading(true);
      setReminderJobsError(""); setReminderJobs([]); setReminderJobsTotal(0);
      try {
        const data = await getLeadQualificationReminderJobs(flowDefinitionId, {
          page,
          pageSize: reminderJobsPageSize,
          ...(status ? { status } : {}),
        });
        if (!current()) return;
        setReminderJobs(Array.isArray(data.items) ? data.items : []);
        setReminderJobsTotal(Number(data.total) || 0);
        setReminderJobsPage(Number(data.page) || page);
      } catch (err) {
        if (!current()) return;
        setReminderJobs([]);
        setReminderJobsTotal(0);
        setReminderJobsError(err?.response?.data?.message || "Failed to load reminder jobs.");
      } finally {
        if (current()) setReminderJobsLoading(false);
      }
    },
    [reminderJobsPage, reminderJobsPageSize, reminderJobsStatus, request]
  );

  React.useEffect(() => {
    loadContacts();
    loadSenders();
  }, [loadContacts, loadSenders]);

  const load = React.useCallback(async () => {
    const current = request("debug");
    const cid = contactId.trim();
    const pid = phoneNumberId.trim();
    if (!cid || !pid) {
      toast.error("ContactId and PhoneNumberId are required.");
      return;
    }
    setLoading(true); setLoadError(""); setResult(null); setSearched(false);
    try {
      const data = await getAiLeadQualificationDebug({ contactId: cid, phoneNumberId: pid });
      if (!current()) return;
      setResult(data);
      setSearched(true);
      setReminderJobsPage(1);
      await loadReminderJobs(data?.session?.flowDefinitionId, { page: 1, status: reminderJobsStatus });
    } catch (err) {
      if (current()) setLoadError(err?.response?.data?.message || "Failed to load debug data.");
    } finally {
      if (current()) setLoading(false);
    }
  }, [contactId, phoneNumberId, loadReminderJobs, reminderJobsStatus, request]);

  const onReset = async () => {
    const cid = contactId.trim();
    const pid = phoneNumberId.trim();
    if (!cid || !pid) {
      toast.error("ContactId and PhoneNumberId are required.");
      return;
    }
    if (!window.confirm("Reset the active lead qualification session for this thread only?"))
      return;
    setResetting(true);
    try {
      await resetAiLeadQualificationSession({ contactId: cid, phoneNumberId: pid });
      toast.success("Lead session reset.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to reset session.");
    } finally {
      setResetting(false);
    }
  };

  const onReminderStatusChange = async e => {
    const nextStatus = e.target.value;
    setReminderJobsStatus(nextStatus);
    setReminderJobsPage(1);
    await loadReminderJobs(reminderFlowDefinitionId, { page: 1, status: nextStatus });
  };

  const refreshReminderJobs = async () => {
    await loadReminderJobs(reminderFlowDefinitionId, {
      page: reminderJobsPage,
      status: reminderJobsStatus,
    });
  };

  const goToReminderPage = async nextPage => {
    const page = Math.max(1, nextPage);
    setReminderJobsPage(page);
    await loadReminderJobs(reminderFlowDefinitionId, {
      page,
      status: reminderJobsStatus,
    });
  };

  const session = result?.session ?? null;
  const actionLogs = Array.isArray(result?.actionLogs) ? result.actionLogs : EMPTY_ARRAY;
  const decisionTraces = React.useMemo(
    () => collectDecisionTraces(result, actionLogs),
    [result, actionLogs]
  );
  const reminderFlowDefinitionId = session?.flowDefinitionId ?? null;
  const reminderPageCount = Math.max(1, Math.ceil((reminderJobsTotal || 0) / reminderJobsPageSize));

  return (
    <div className="min-h-[calc(100vh-80px)] bg-slate-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-7xl space-y-5">

        {/* ── header ── */}
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Advanced diagnostics</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Advanced support tools: inspect a real contact thread and its action history. To try invented conversations without live changes, use the conversation walkthrough in Test your assistant.
          </p>
        </div>

        {/* ── search bar ── */}
        <AiLoadError message={contactsError} onRetry={loadContacts} loading={contactsLoading} label="contacts" />
        <AiLoadError message={sendersError} onRetry={loadSenders} loading={sendersLoading} label="senders" />
        <AiLoadError message={loadError} onRetry={load} loading={loading} label="thread evidence" />
        <Panel>
          <div className="flex flex-wrap items-end gap-3 p-4">
            <div className="flex-1 min-w-[180px]">
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Contact no
              </label>
              <select
                aria-label="Contact no"
                value={contactId}
                onChange={e => setContactId(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              >
                <option value="">
                  {contactsLoading ? "Loading contacts..." : "Select a contact number"}
                </option>
                {normalizedContacts.map(contact => (
                  <option key={contact.contactId} value={contact.contactId}>
                    {contact.phoneNumber} · {contact.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="mb-1 block text-xs font-medium text-slate-600">
                WhatsApp sender ID
              </label>
              <select
                aria-label="WhatsApp sender ID"
                value={phoneNumberId}
                onChange={e => setPhoneNumberId(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              >
                <option value="">
                  {sendersLoading ? "Loading senders..." : "Select a sender"}
                </option>
                {normalizedSenders.map(sender => (
                  <option key={sender.phoneNumberId} value={sender.phoneNumberId}>
                    {sender.displayLabel}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={load}
                disabled={loading || !contactId.trim() || !phoneNumberId.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Search className="h-4 w-4" />
                {loading ? "Loading…" : "Search"}
              </button>
              {canManage && (
                <button
                  type="button"
                  onClick={onReset}
                  disabled={resetting || !contactId.trim() || !phoneNumberId.trim()}
                  className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RotateCcw className="h-4 w-4" />
                  {resetting ? "Resetting…" : "Reset session"}
                </button>
              )}
            </div>
          </div>
          <div className="px-4 pb-4 text-xs text-slate-400">
            Search and reset operate on the selected contact number and WhatsApp sender ID.
          </div>
        </Panel>

        {result?.outcome ? <section aria-label="Saved business outcome" className="rounded-xl border border-slate-200 bg-white p-5 text-sm">
          <h2 className="font-semibold text-slate-900">Saved business outcome</h2>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            <div>Qualification record: <strong>{result.outcome.sessionStatus || "Not recorded"}</strong></div>
            <div>Inbox handover: <strong>{{ PendingUnassigned: "Pending — unassigned", Assigned: "Assigned", Resolved: "Resolved", NotRecorded: "Not recorded" }[result.outcome.handoverState] || "Unknown"}</strong></div>
            <div>Assigned owner: <span className="font-mono text-xs">{result.outcome.assignedOwnerUserId || "Unassigned"}</span></div>
          </div>
          <p className="mt-3 text-slate-600">{result.outcome.nextAction}</p>
          <p className="mt-2 text-xs text-slate-500">Linked inquiry: {result.outcome.enquiryId || "No verified link"}. Qualification completion alone does not confirm a booking or prove a follow-up exists.</p>
          {result.outcome.sessionId ? <div className="mt-3"><h3 className="mb-2 text-xs font-medium text-slate-600">Latest saved qualification details</h3><JsonKv raw={result.outcome.collectedFieldsJson} emptyLabel="No saved fields" /></div> : null}
        </section> : null}

        {/* ── session panel ── */}
        {(searched || session) && (
          <Panel>
            <PanelHeader
              title="Active Session"
              subtitle="Latest lead qualification session for this thread"
              actions={
                result && (
                  <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
                  >
                    <RefreshCcw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                  </button>
                )
              }
            />
            <div className="p-5">
              {session ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">Status</div>
                      <StatusBadge value={session.status} />
                    </div>
                    <StatCard label="Current step" value={session.currentStep} />
                    <StatCard label="Last asked field" value={session.lastAskedField} mono />
                    <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">Updated</div>
                      <div className="text-sm font-medium text-slate-900">
                        {relativeTime(session.updatedAtUtc)}
                      </div>
                      <div className="text-xs text-slate-400">{fmtDate(session.updatedAtUtc)}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Captured fields
                      </div>
                      <JsonKv raw={session.collectedFieldsJson} emptyLabel="No fields captured yet" />
                    </div>
                    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Missing fields
                      </div>
                      <JsonList raw={session.missingFieldsJson} emptyLabel="No missing fields" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Pending field confirmation
                      </div>
                      <JsonPretty
                        raw={session.pendingFieldConfirmationJson}
                        emptyLabel="No pending field confirmation"
                      />
                    </div>
                    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Pending summary checkpoint
                      </div>
                      <JsonPretty
                        raw={session.pendingSummaryCheckpointJson}
                        emptyLabel="No pending summary checkpoint"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="mb-2 text-2xl">💬</div>
                  <div className="text-sm font-medium text-slate-700">No active session</div>
                  <div className="mt-0.5 text-xs text-slate-400">
                    No active qualification session is available. Completed or closed records, when available, appear in Saved business outcome above.
                  </div>
                </div>
              )}
            </div>
          </Panel>
        )}

        {/* ── action logs ── */}
        {(searched || reminderFlowDefinitionId || reminderJobs.length > 0 || reminderJobsError) && (
          <Panel>
            <PanelHeader
              title="Reminder Jobs"
              subtitle="Read-only visibility into scheduled and processed lead qualification reminders"
              actions={
                <button
                  type="button"
                  onClick={refreshReminderJobs}
                  disabled={reminderJobsLoading || !reminderFlowDefinitionId}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RefreshCcw className={`h-3.5 w-3.5 ${reminderJobsLoading ? "animate-spin" : ""}`} />
                  Refresh
                </button>
              }
            />
            <div className="space-y-4 p-5">
              <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Reminder jobs are read-only here. Jobs are created after eligible lead-question sends.
                Text reminders are sent only if the reminder worker is explicitly enabled. Media/template
                reminders are not active yet.
              </div>

              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[180px]">
                  <label className="mb-1 block text-xs font-medium text-slate-600">Status</label>
                  <select
                    aria-label="Reminder status"
                    value={reminderJobsStatus}
                    onChange={onReminderStatusChange}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  >
                    <option value="">All statuses</option>
                    <option value="Pending">Pending</option>
                    <option value="Processing">Processing</option>
                    <option value="Sent">Sent</option>
                    <option value="Skipped">Skipped</option>
                    <option value="Failed">Failed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="text-xs text-slate-400">
                  {reminderJobsTotal} total reminder job{reminderJobsTotal === 1 ? "" : "s"}
                </div>
              </div>

              {reminderJobsError ? (
                <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  <div>{reminderJobsError}</div>
                  <button
                    type="button"
                    onClick={refreshReminderJobs}
                    className="mt-2 inline-flex items-center rounded-md border border-rose-200 bg-white px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
                  >
                    Retry
                  </button>
                </div>
              ) : null}

              <div className="overflow-x-auto rounded-lg border border-slate-100">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
                      <th className="px-4 py-2.5">Created / Due</th>
                      <th className="px-4 py-2.5">Status</th>
                      <th className="px-4 py-2.5">Seq</th>
                      <th className="px-4 py-2.5">Last asked field</th>
                      <th className="px-4 py-2.5">Pending question</th>
                      <th className="px-4 py-2.5">Attempts</th>
                      <th className="px-4 py-2.5">Failure / skip reason</th>
                      <th className="px-4 py-2.5">Contact</th>
                      <th className="px-4 py-2.5">Session</th>
                      <th className="px-4 py-2.5">Outbound question log</th>
                      <th className="px-4 py-2.5">Sent text log</th>
                      <th className="px-4 py-2.5">Sent media log</th>
                      <th className="px-4 py-2.5">Next attempt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                      {reminderJobsLoading ? (
                        <tr>
                          <td colSpan={13} className="px-4 py-10 text-center text-sm text-slate-500">
                            Loading reminder jobs...
                          </td>
                        </tr>
                      ) : reminderJobs.length === 0 ? (
                        <tr>
                          <td colSpan={13} className="px-4 py-10 text-center">
                            <div className="text-sm font-medium text-slate-700">
                              {reminderJobsError ? "Reminder jobs are unavailable. Retry the load." : "No reminder jobs found yet."}
                            </div>
                          <div className="mt-0.5 text-xs text-slate-400">
                            Reminder jobs appear after eligible lead-question sends.
                          </div>
                        </td>
                      </tr>
                    ) : (
                      reminderJobs.map(job => <ReminderJobRow key={job.id} job={job} />)
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
                <div>
                  Page {reminderJobsPage} of {reminderPageCount}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => goToReminderPage(reminderJobsPage - 1)}
                    disabled={reminderJobsLoading || reminderJobsPage <= 1}
                    className="rounded-md border border-slate-200 px-2.5 py-1 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => goToReminderPage(reminderJobsPage + 1)}
                    disabled={reminderJobsLoading || reminderJobsPage >= reminderPageCount}
                    className="rounded-md border border-slate-200 px-2.5 py-1 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </Panel>
        )}

        {(searched || actionLogs.length > 0) && (
          <Panel>
            <PanelHeader
              title="Decision Traces"
              subtitle="Compact planner and policy traces from the latest debug payload"
            />
            <div className="space-y-4 p-5">
              {DECISION_TRACE_FIELDS.some(([key]) => decisionTraces[key] != null) ? (
                DECISION_TRACE_FIELDS.map(([key, label]) =>
                  decisionTraces[key] != null ? (
                    <div key={key} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        {label}
                      </div>
                      <JsonPretty raw={decisionTraces[key]} emptyLabel={`No ${label.toLowerCase()}`} />
                    </div>
                  ) : null
                )
              ) : (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                  No decision traces available.
                </div>
              )}
            </div>
          </Panel>
        )}

        {(searched || actionLogs.length > 0) && (
          <Panel>
            <PanelHeader
              title="Action Logs"
              subtitle={`${actionLogs.length} entr${actionLogs.length === 1 ? "y" : "ies"} — click a row to expand planner + NR detail`}
            />
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-2.5">Time</th>
                    <th className="px-4 py-2.5">Intent / Step</th>
                    <th className="px-4 py-2.5">Action</th>
                    <th className="px-4 py-2.5">Preview</th>
                    <th className="px-4 py-2.5">Send</th>
                    <th className="px-3 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {actionLogs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center">
                        <div className="text-2xl">📋</div>
                        <div className="mt-1.5 text-sm font-medium text-slate-700">No action logs found</div>
                        <div className="mt-0.5 text-xs text-slate-400">
                          Run a lead qualification dry-run for this thread to see entries here.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    actionLogs.map(log => <ActionLogRow key={log.id} log={log} />)
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        {/* ── initial empty state ── */}
        {!searched && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Search className="mb-3 h-10 w-10 text-slate-300" />
            <div className="text-sm font-medium text-slate-600">Select a contact number and WhatsApp sender ID to begin</div>
            <div className="mt-0.5 text-xs text-slate-400">
              Session state and action logs will appear here.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default withAiAgentScope(AiLeadQualificationDebugPage);
